import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type SendDatabaseCreatedViewEventData } from '@databases/analytics/src/types';
import { getUniqueViewName } from '@databases/api-views/utils/viewName';
import {
  getFirstEligibleGroupField,
  getGroupField,
  getSubgroupField,
} from '@databases/grouping';
import { type AppMode } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonContext } from '@databases/skeleton';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import {
  DatabaseGroupSortType,
  DatabaseLayoutType,
  type DatabaseView,
  ViewId,
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { createView, getDefaultView } from '@databases/utils/database/view/view';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import afterFrame from 'afterframe';
import React, { type FunctionComponent, useCallback, lazy, Suspense } from 'react';
import { useIntl } from 'react-intl-next';

import { dynamicImport } from '@canvas/dynamic-import';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { type DatabaseActionKey } from '../database/DatabaseActions';

import ToolbarSkeleton from './ToolbarSkeleton';

// Lazy-loaded components
const EmbedReferenceToolbar = lazy(() =>
  dynamicImport(() => import('./toolbars/EmbedReferenceToolbar'))
);
const EmbedToolbar = lazy(() => dynamicImport(() => import('./toolbars/EmbedToolbar')));
const MobileToolbar = lazy(() => dynamicImport(() => import('./toolbars/MobileToolbar')));
const NativeEmbedToolbar = lazy(() =>
  dynamicImport(() => import('./toolbars/NativeEmbedToolbar'))
);
const StandaloneToolbar = lazy(() =>
  dynamicImport(() => import('./toolbars/StandaloneToolbar'))
);

export type ToolbarProps = ToolbarContainerProps;

export type OpenViewSettingsPanel = (setting: ViewPanelSettingType | null) => void;
export type HandleAddView = (
  type: DatabaseLayoutType,
  source: SendDatabaseCreatedViewEventData['source']
) => void;

export interface ToolbarInternalProps extends ToolbarContainerProps {
  clearUnsavedView: () => void;
  databaseDefaultView: DatabaseView;
  fields: DatabaseField<DatabaseFieldType>[];
  groupField?: DatabaseField<DatabaseFieldType>;
  handleAddView: HandleAddView;
  openViewSettingsPanel: OpenViewSettingsPanel;
  resolvedView: DatabaseView;
  savedViews: DatabaseView[];
  setViewId: (viewId: string) => void;
  subgroupField?: DatabaseField<DatabaseFieldType>;
  view: DatabaseView;
  unsavedView: DatabaseView | null;
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const Toolbar: FunctionComponent<ToolbarInternalProps> = ({
  actions,
  canEditContent,
  canEditFields,
  contentId,
  databaseDefaultView,
  fields,
  groupField,
  handleAddView,
  mode,
  openViewSettingsPanel,
  referencing,
  resolvedView,
  savedViews,
  subgroupField,
  view,
  unsavedView,
}) => {
  const renderToolbar = () => {
    switch (mode) {
      case 'MOBILE':
        return (
          <MobileToolbar
            canEditFields={canEditFields}
            contentId={contentId}
            defaultView={resolvedView}
            handleAddView={handleAddView}
            openViewSettingsPanel={openViewSettingsPanel}
            viewSettings={view.settings}
          />
        );

      case 'NATIVE_EMBED':
        return (
          <NativeEmbedToolbar
            actions={actions}
            canEditContent={canEditContent}
            canEditFields={canEditFields}
            contentId={contentId}
            databaseDefaultView={databaseDefaultView}
            defaultView={resolvedView}
            fieldsLength={fields.length}
            groupField={groupField}
            handleAddView={handleAddView}
            openViewSettingsPanel={openViewSettingsPanel}
            subgroupField={subgroupField}
            viewSettings={view.settings}
            savedViews={savedViews}
            selectedView={view}
            unsavedView={unsavedView}
          />
        );
      case 'EMBED':
        if (referencing) {
          return (
            <EmbedReferenceToolbar
              actions={actions}
              contentId={contentId}
              handleAddView={handleAddView}
              openViewSettingsPanel={openViewSettingsPanel}
              defaultView={resolvedView}
            />
          );
        }

        return (
          <EmbedToolbar
            actions={actions}
            canEditContent={canEditContent}
            canEditFields={canEditFields}
            contentId={contentId}
            defaultView={resolvedView}
            fieldsLength={fields.length}
            groupField={groupField}
            handleAddView={handleAddView}
            openViewSettingsPanel={openViewSettingsPanel}
            subgroupField={subgroupField}
            viewSettings={view.settings}
          />
        );

      case 'STANDALONE':
      default:
        return (
          <StandaloneToolbar
            actions={actions}
            canEditContent={canEditContent}
            canEditFields={canEditFields}
            contentId={contentId}
            defaultView={resolvedView}
            fieldsLength={fields.length}
            groupField={groupField}
            handleAddView={handleAddView}
            openViewSettingsPanel={openViewSettingsPanel}
            subgroupField={subgroupField}
            viewSettings={view.settings}
          />
        );
    }
  };

  return (
    <Suspense
      fallback={
        <SkeletonContext isEnabled={true}>
          <ToolbarSkeleton />
        </SkeletonContext>
      }
    >
      {renderToolbar()}
    </Suspense>
  );
};

export { Toolbar };

export interface ToolbarContainerProps {
  actions?: DatabaseActionKey[];
  canEditContent: boolean;
  canEditFields: boolean;
  contentId: string;
  mode: AppMode;
  referencing?: boolean;
}

export const ToolbarContainer: FunctionComponent<ToolbarProps> = (props) => {
  const { contentId, canEditFields } = props;
  const client = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const { setActivePanelSetting } = useViewUiState();
  const intl = useIntl();
  const { showPanel } = usePanel();

  const { view, setViewId, resolvedView, clearUnsavedView, unsavedView } = useView();
  const {
    savedViews,
    fields,
    groupField,
    subgroupField,
    firstEligibleGroupField,
    databaseDefaultView,
  } = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const groupField = getGroupField(databaseDraft, view.settings);
    const subgroupField = getSubgroupField(databaseDraft, view.settings);
    const firstEligibleGroupField = getFirstEligibleGroupField(databaseDraft)?.id;
    const databaseDefaultView = getDefaultView(databaseDraft);

    return {
      savedViews: databaseDraft.views,
      fields: getDatabaseFields(databaseDraft),
      groupField,
      subgroupField,
      firstEligibleGroupField,
      databaseDefaultView,
    };
  });

  const openViewSettingsPanel = useCallback(
    (activeViewSetting: ViewPanelSettingType | null) => {
      const startedRenderingAt = performance.now();

      setActivePanelSetting(activeViewSetting);
      showPanel('ViewSettingsPanel', {
        canEditViews: canEditFields,
      });

      afterFrame(() => {
        const reliability = ReliabilityMetrics.getInstance();

        reliability.successWithDuration(
          MetricName.DATABASE_PANEL_VIEW_SETTINGS_OPEN,
          startedRenderingAt,
          performance.now()
        );
      });

      // If table layout header cell selection is active,
      // remove selection border on view settings button click
      dispatch(deselectAll());
    },
    [setActivePanelSetting, showPanel, canEditFields, dispatch]
  );

  const handleAddView = useCallback(
    (type: DatabaseLayoutType, source: SendDatabaseCreatedViewEventData['source']) => {
      const baseView = createView();

      const newViewName = getUniqueViewName(
        baseView,
        savedViews.map((v) => v.name),
        intl
      );

      const boardGrouping = {
        grouping: {
          groupBy: {
            fieldId: firstEligibleGroupField ?? '',
            manualGroupOrder: [],
            sortType: DatabaseGroupSortType.MANUAL,
          },
        },
      };

      const newView = {
        ...baseView,
        name: newViewName,
        settings: {
          ...baseView.settings,
          layout: {
            ...baseView.settings.layout,
            type,
          },
          ...(type === DatabaseLayoutType.BOARD && boardGrouping),
        },
      };

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.ADD_SAVED_VIEW,
          view: newView,
        })
      );

      if (view.id === ViewId.UNSAVED) {
        clearUnsavedView();
      }

      setViewId(newView.id);

      openViewSettingsPanel(ViewPanelSettingType.LAYOUT);

      client.sendDatabaseCreateViewEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: newView.id,
        source,
        layoutType: newView.settings.layout.type,
      });
    },
    [
      clearUnsavedView,
      client,
      contentId,
      dispatch,
      firstEligibleGroupField,
      intl,
      openViewSettingsPanel,
      savedViews,
      setViewId,
      view.id,
    ]
  );

  return (
    <Toolbar
      {...props}
      clearUnsavedView={clearUnsavedView}
      databaseDefaultView={databaseDefaultView}
      fields={fields}
      groupField={groupField}
      handleAddView={handleAddView}
      openViewSettingsPanel={openViewSettingsPanel}
      resolvedView={resolvedView}
      savedViews={savedViews}
      setViewId={setViewId}
      subgroupField={subgroupField}
      view={view}
      unsavedView={unsavedView}
    />
  );
};

export default ToolbarContainer;
